import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { logger } from '@/logger';
import { useLatestProgress } from '@/spaces/campaigns/useLatestProgress';
import {
	proposalHeadline,
	usePendingProposals,
	type PendingProposal,
} from '@/spaces/campaigns/usePendingProposals';
import { useStrategies } from '@/spaces/campaigns/useStrategies';
import { useStrategyActions } from '@/spaces/campaigns/useStrategyActions';
import { markSetupRevealPending } from '@/spaces/chat/messages/setup/useSetupIntroReveal.ts';
import { cn } from '@/utils/cn';
import { trackEvent } from '@/utils/fullstory';

import { DockBar } from './DockBar';
import { DockMenu, type DockRect, type View } from './DockMenu';
import { identityMap } from './orbIdentity';
import { type DockAnimKind, runDockMorph } from './strategyDockAnimation';
import { dockIconRows, openFlags, visibleDockRows } from './strategyDockData';
import { useDockMembershipMotion } from './useDockMembershipMotion';

// The menu is a centered sheet with side gutters and a capped width, anchored at the resting
// bar's top edge and growing down to (but never past) the chat column's bottom edge.
const MENU_MAX_W = 640;
const MENU_SIDE_GUTTER = 24;
const MENU_BOTTOM_GAP = 16;

// Derive the menu rect from the chat column + the resting bar. The menu's top edge is the
// BAR's top edge, so the two form ONE connected surface with the dock sitting on top of the
// menu (they share the same gray fill) — not a detached dropdown floating below it. The bar's
// height becomes the menu's top inset so its content starts below the dock. Capped in width
// and in height so it stays a menu within the column, never a full-screen surface. `height`
// is a MAX — the menu shrinks to its content.
function menuRectFrom(
	column: HTMLElement | null,
	bar: HTMLElement | null,
): DockRect | null {
	if (!column || !bar) return null;
	const c = column.getBoundingClientRect();
	const b = bar.getBoundingClientRect();
	const width = Math.min(MENU_MAX_W, c.width - MENU_SIDE_GUTTER * 2);
	const left = c.left + (c.width - width) / 2;
	const top = b.top;
	const height = c.top + c.height - top - MENU_BOTTOM_GAP;
	return { left, top, width, height, topInset: b.height };
}

// The persistent strategy manager, mounted inside Chat. A resting dock bar (pinned strategies
// as orbs) that stays visible and sits ON TOP of the MENU surface that opens under it — one
// connected element, not a detached dropdown — for viewing status / managing / creating
// strategies. Owns the data + pin hooks at this stable level so a chat-thread switch never
// remounts them. Gated on the 'strategies' flag.
export function StrategyDock({
	// The chat column element the menu is measured against (ChatLayout), so it centers on
	// the chat area and shifts with the history panel / resizable split.
	columnRef,
}: {
	columnRef: React.RefObject<HTMLElement | null>;
}) {
	const bridgeApi = useBridgeApi();
	const enabled = useFeatureFlag('strategies');
	const userId = useUserId();
	const openChatPanel = useOpenChatPanel();
	const strategies = useStrategies({ enabled });
	// Same revision counter the strategy list watches: every write that raises a proposal also
	// touches the strategy doc, so one push signal keeps both fresh.
	const proposalsState = usePendingProposals({
		enabled,
		revision: bridgeApi.user_state?.strategy_dock_last_updated ?? 0,
	});
	// The measured north-star values behind the card gauges, on the same revision signal.
	const progressState = useLatestProgress({
		enabled,
		revision: bridgeApi.user_state?.strategy_dock_last_updated ?? 0,
	});
	const {
		generate,
		regenerate,
		activate: activateStrategy,
		deactivate: deactivateStrategy,
		acknowledge: acknowledgeStrategy,
	} = useStrategyActions();

	const [isOpen, setIsOpen] = useState(false);
	// Which view the current open request wants. ONE channel for list / detail / create, so a
	// create intent can never be overwritten by an effect that only knows the other two.
	const [intent, setIntent] = useState<View>({ kind: 'list' });
	// Bumped on every open request so the menu re-focuses even when the same orb is clicked
	// again (the bar persists, so orbs stay clickable while the menu is open).
	const [focusNonce, setFocusNonce] = useState(0);
	const [rect, setRect] = useState<DockRect | null>(null);
	// Which motion path the last open/close actually took — surfaced on the menu frame as
	// data-dock-anim so an e2e can assert the morph deterministically (never a screenshot).
	const animKindRef = useRef<DockAnimKind>('morph');
	// The resting bar is centered on the chat COLUMN, not the container — so it tracks
	// right when the history (chat list) panel opens and follows the resizable split.
	const barRef = useRef<HTMLDivElement>(null);
	const [pillLeft, setPillLeft] = useState<number | null>(null);

	// What the owner actually sees: their running strategies, the ones they ran and stopped,
	// and the newest batch still awaiting a decision. Generating again replaces the previous
	// batch's untouched suggestions. The bar and the menu read from the SAME curated set, so
	// an orb can never point at a strategy the list below it has dropped.
	const visibleItems = useMemo(
		() => visibleDockRows(strategies.strategies),
		[strategies.strategies],
	);
	const barItems = useMemo(() => dockIconRows(visibleItems), [visibleItems]);
	// Per-strategy pending-proposal counts, so the COLLAPSED orb and the open strategy card agree
	// about whether a strategy is waiting on the owner. The orb read only `attention_flags`, so a
	// tick whose only output was a proposal changed nothing the owner could see without opening the
	// menu first.
	const pendingProposalsByStrategyId = useMemo(() => {
		const counts: Record<string, number> = {};
		for (const proposal of proposalsState.proposals) {
			if (!proposal.strategy_id) continue;
			counts[proposal.strategy_id] = (counts[proposal.strategy_id] ?? 0) + 1;
		}
		return counts;
	}, [proposalsState.proposals]);
	// Hand out a face to any strategy that does not have one yet, from the owner's WHOLE history.
	// The surfaces below hold differently filtered slices of it, so none of them could work out a
	// stable rank on its own — and a face, once given, is never recomputed.
	useMemo(() => identityMap(strategies.strategies), [strategies.strategies]);

	// True once the strategy list has answered at least once. Latched, and deliberately NOT
	// derived from `error`: a failed refresh keeps the last good list, so keying on error would
	// blank a healthy dock on one transient poll failure mid-session.
	const settledRef = useRef(false);
	if (!strategies.loading) settledRef.current = true;
	const settled = settledRef.current;

	// Which orbs are arriving and which are leaving. Owned here because DockBar remounts
	// whenever the menu opens (the bar moves into the menu surface).
	const { enteringIds, leavingItems } = useDockMembershipMotion(
		barItems,
		settled,
	);

	// Keep the menu glued to the bar as the column reflows. A ResizeObserver on the column is
	// required, not just window events: opening the history panel (Cmd+/) or an agent-pushed
	// review panel re-flexes ChatLayout via pure React state, firing NO window resize/scroll —
	// the bar re-centers (its own observer below) while a window-only listener would leave the
	// menu at stale geometry, detached from the bar and overhanging the narrowed column.
	useEffect(() => {
		if (!isOpen) return;
		const update = () =>
			setRect(menuRectFrom(columnRef.current, barRef.current));
		update();
		const column = columnRef.current;
		const ro =
			typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
		if (column) ro?.observe(column);
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, true);
		return () => {
			ro?.disconnect();
			window.removeEventListener('resize', update);
			window.removeEventListener('scroll', update, true);
		};
	}, [isOpen, columnRef]);

	// Center the resting bar on the chat COLUMN (relative to its offset parent), so it
	// shifts right with the history panel and follows the split. A ResizeObserver on the
	// column tracks the panel's open/close width animation and drag; window resize covers
	// viewport changes. Falls back to container-centered (pillLeft=null → CSS left-1/2).
	useEffect(() => {
		if (!enabled) return;
		const column = columnRef.current;
		if (!column) return;
		const update = () => {
			const parent = barRef.current?.offsetParent as HTMLElement | null;
			if (!parent) return;
			const c = column.getBoundingClientRect();
			const p = parent.getBoundingClientRect();
			setPillLeft(c.left - p.left + c.width / 2);
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(column);
		window.addEventListener('resize', update);
		return () => {
			ro.disconnect();
			window.removeEventListener('resize', update);
		};
	}, [enabled, columnRef]);

	// Closing morphs too: the View Transition snapshots the menu frame and tweens it back into
	// the dock. A CSS exit animation can't do this — React unmounts the menu synchronously.
	const close = useCallback(() => {
		animKindRef.current = runDockMorph(() => setIsOpen(false));
	}, []);

	// Open the menu on a specific view. Every entry point routes through here: the manage
	// glyph (list), an orb (that strategy's detail), and the ghost slot (create).
	const openOn = useCallback(
		(next: View, from: 'manage' | 'icon' | 'slot') => {
			const r = menuRectFrom(columnRef.current, barRef.current);
			if (!r) return;
			trackEvent('Strategy Dock Opened', { from });
			// Seed rect/view/nonce OUTSIDE the transition so the menu mounts already positioned
			// in the new frame — a post-mount reposition would jump mid-morph.
			setIntent(next);
			setRect(r);
			setFocusNonce((n) => n + 1);
			// Already open (a re-focus) → no box morph to run, just swap the view.
			if (isOpen) return;
			animKindRef.current = runDockMorph(() => setIsOpen(true));
		},
		[columnRef, isOpen],
	);

	const open = useCallback(
		(id?: string) => {
			// The manage affordance toggles the menu; a strategy orb opens (or, while already
			// open, refocuses) its detail. Clicking manage while open closes.
			if (id === undefined && isOpen) {
				close();
				return;
			}
			// An orb with an unanswered request opens the notification list. Reading a request
			// stamps it, so the orb stops asking, but the row stays until it is answered.
			if (id) {
				const item = strategies.strategies.find((s) => s.id === id);
				if (item && openFlags(item).length > 0) {
					openOn({ kind: 'flags', id }, 'icon');
					return;
				}
			}
			openOn(
				id ? { kind: 'detail', id } : { kind: 'list' },
				id ? 'icon' : 'manage',
			);
		},
		[isOpen, close, openOn, strategies],
	);

	// The ghost slot: start creating a strategy, and the ONLY route to it once the owner has
	// strategies. Opens the menu straight on its create view, never a bare generate call.
	const openCreate = useCallback(() => {
		openOn({ kind: 'create' }, 'slot');
	}, [openOn]);

	// Mirror CampaignSpace.handleGenerate: refresh BEFORE landing so the list shows the
	// new batch's rows present (else the empty-batch "no longer available" flash appears).
	const handleGenerate = useCallback(async () => {
		trackEvent('Strategy Generate Clicked', { model_choice: 'gpt' });
		await generate({ businessId: userId, modelChoice: 'gpt' });
		await strategies.refresh();
	}, [generate, userId, strategies]);

	// Rebuild one dead variant in place. It throws on refusal and this does NOT catch: the
	// failed screen shows the reason, and the server refuses while a sibling is still building.
	const handleRegenerate = useCallback(
		async (strategyId: string) => {
			trackEvent('Strategy Regenerate Clicked', { strategy_id: strategyId });
			await regenerate(strategyId);
			await strategies.refresh();
		},
		[regenerate, strategies],
	);

	// Activation flips the run server-side; nothing polls for that transition (the list only
	// polls while something is generating), so refresh here or the card keeps offering
	// "Activate" and the row never moves into the active group.
	const handleActivate = useCallback(
		async (strategyId: string) => {
			trackEvent('Strategy Activated', { strategy_id: strategyId });
			await activateStrategy(strategyId);
			await strategies.refresh();
		},
		[activateStrategy, strategies],
	);

	// Pausing is the inverse of activating and refreshes the same way: nothing polls for the
	// status flip, so without this the card keeps offering Pause on a stopped strategy.
	const handlePause = useCallback(
		async (strategyId: string) => {
			trackEvent('Strategy Paused', { strategy_id: strategyId });
			await deactivateStrategy(strategyId);
			await strategies.refresh();
		},
		[deactivateStrategy, strategies],
	);

	// Marking a request read is not the owner's goal — reaching the conversation is. So a
	// failed stamp must not stop the thread from opening. It leaves the request unread, which
	// keeps it on the dock, which is the honest outcome: nothing was answered.
	const handleAcknowledgeFlag = useCallback(
		(strategyId: string, flagId: string) => {
			void acknowledgeStrategy(strategyId, flagId)
				.then(() => strategies.refresh())
				.catch((err) => {
					logger.error('Failed to mark a strategy request read', err);
				});
		},
		[acknowledgeStrategy, strategies],
	);

	// Reading a request opens a thread ABOUT that request. Linked by the FLAG id, not the
	// strategy's, so `do_create_chat`'s {type,id} dedup returns the owner to the same
	// conversation for the same request, and two requests on one strategy get two threads.
	// The strategy id rides in metadata so the panel can open beside the thread.
	//
	// No `initial_message`: the reducer appends one every time it reuses a thread, so a
	// second press would repeat the same sentence. The opening line below is
	// `initial_agent_message`, which the reducer guards by id. The agent also gets the request
	// from its injected context, which lets the owner answer in any other thread.
	const handleOpenFlagThread = useCallback(
		(strategyId: string, flagId: string) => {
			const item = strategies.strategies.find((s) => s.id === strategyId);
			const flag = (item?.attention_flags ?? []).find((f) => f.id === flagId);
			trackEvent('Strategy Request Opened', {
				strategy_id: strategyId,
				flag_id: flagId,
				kind: flag?.kind,
			});
			const request = flag?.message?.trim();
			bridgeApi.sendAction({
				type: 'do_create_chat',
				linked_item: {
					type: 'strategy_flag',
					id: flagId,
					metadata: {
						// No stand-in noun. design.md forbids filling a name-shaped hole with one, and
						// a request always has a message, so a title is only absent when there is
						// genuinely nothing to name.
						...(request ? { title: request.slice(0, 80) } : {}),
						strategy_id: strategyId,
						...(item?.wish ? { strategy_title: item.wish } : {}),
					},
				},
				// The request's own words, as the assistant's opening line. They exist on the server
				// already, so the thread speaks the moment it opens instead of sitting empty for the
				// ~47s a turn takes to produce its first word.
				//
				// The id is DERIVED from the flag, so pressing this card again returns the owner to
				// the same thread and does not repeat the line.
				...(request
					? {
							initial_agent_message: {
								id: `flag-intro-${flagId}`,
								content: request,
							},
						}
					: {}),
			});
			close();
		},
		[bridgeApi, close, strategies],
	);

	// The owner's own part of a campaign. The card opens a conversation about that one ask,
	// rather than the strategy panel: the ask is a thing to answer, not a thing to read.
	//
	// The strategy rides in metadata, exactly as `handleOpenFlagThread` above does it, so the
	// thread can describe the plan the part serves. It is resolved from the ask id rather than
	// passed in, because both dock rows reach here through a two-argument callback that five
	// call sites share.
	//
	// No `initial_message`: the reducer stores that as a USER message and appends one every time
	// it reuses a thread, so returning to the same part printed the same sentence again. The
	// opening line below is `initial_agent_message`, which the reducer guards by id.
	//
	// The line is not decoration. A thread that opens with no messages renders the empty-chat
	// layout, so the owner pressed their own part and read a blank new chat instead. The ask's
	// own words are already on the card, so the thread can state them at once. This mirrors
	// `handleOpenFlagThread` above, and the agent still gets the part from its injected context.
	const handleOpenOwnerAsk = useCallback(
		(actionId: string, message: string) => {
			trackEvent('Owner Ask Opened', { action_id: actionId });
			const owner = strategies.strategies.find((s) =>
				(s.attention_flags ?? []).some((f) => f.action_id === actionId),
			);
			const ask = message.trim();
			bridgeApi.sendAction({
				type: 'do_create_chat',
				linked_item: {
					type: 'owner_action',
					id: actionId,
					metadata: {
						title: message.slice(0, 60),
						...(owner ? { strategy_id: owner.id } : {}),
						...(owner?.wish ? { strategy_title: owner.wish } : {}),
					},
				},
				// The id is DERIVED from the ask, so pressing the same part again returns the owner
				// to the same thread and appends nothing.
				...(ask
					? {
							initial_agent_message: {
								id: `owner-ask-intro-${actionId}`,
								content: ask,
							},
						}
					: {}),
			});
			close();
		},
		[bridgeApi, close, strategies],
	);

	// A campaign that activation built and nobody has set up yet. Activation deliberately
	// opens no thread, so this press is the moment the thread is born. That ordering is also
	// what keeps the campaign thread from carrying a timestamp older than the strategy
	// thread that produced it.
	//
	// The chat id is DERIVED from the process id, never minted. `do_emit_process_setup_panel`
	// creates the chat when it does not know the id, and unlike `do_create_chat` the pair has
	// no dedup of its own. A derived id makes a second press land on the same thread.
	const handleOpenCampaignSetup = useCallback(
		(processId: string, message: string, title: string) => {
			trackEvent('Campaign Setup Opened', { process_id: processId });
			// This press is the only place that knows the owner just asked for this thread, so
			// it is the only honest trigger for the reveal. A thread they open again later, or
			// one an agent opened in the background, shows its words already finished.
			markSetupRevealPending(processId);
			bridgeApi.sendAction({
				type: 'do_load_setup_snapshot',
				process_id: processId,
			});
			bridgeApi.sendAction({
				type: 'do_emit_process_setup_panel',
				chat_id: `setup-chat-${processId}`,
				process_id: processId,
				title,
				// The planner's own opening words, written during activation and stored on the
				// notification. The bridge writes them above the card, so the thread never
				// opens on a bare campaign.
				intro_message: message,
			});
			// Open it. The press already stamped the notification read, so it leaves the dock,
			// and without this the owner presses, the dock closes, and nothing they can see
			// happens. The thread would sit unread with no route back to it.
			//
			// STRICTLY LAST, and the order is load-bearing twice over. `do_switch_chat` seeds a
			// placeholder chat when it does not know the id (chat.ts:949-960). Reduced FIRST,
			// that placeholder makes the panel reducer take its append branch, which writes the
			// card with NO words — the bare campaign this whole change removes. Worse, its post
			// effect would fetch a thread Cosmos never stored, and the resulting
			// `report_chat_load_failed` DELETES the chat and nulls `current_chat_id`
			// (chat.ts:1634-1655). Reduced last, it takes the existing-chat branch and clears
			// `is_unread`, which is what the owner just did.
			bridgeApi.sendAction({
				type: 'do_switch_chat',
				chat_id: `setup-chat-${processId}`,
			});
			close();
		},
		[bridgeApi, close],
	);

	// Answering from the card. Most asks are a yes or a not-now and do not need a
	// conversation. Refreshes the strategy list too, because approving one is what starts
	// campaigns and clears the flag behind the orb.
	const handleRespondProposal = useCallback(
		async (proposalId: string, decision: 'approve' | 'reject') => {
			trackEvent('Strategy Proposal Answered', {
				proposal_id: proposalId,
				decision,
			});
			await proposalsState.respond(proposalId, decision);
			await strategies.refresh();
		},
		[proposalsState, strategies],
	);

	// The long path: a thread about this specific ask, for the ones worth talking through.
	//
	// Linked by the PROPOSAL's id, not the strategy's, so `do_create_chat`'s {type,id} dedup
	// lands the owner back in the same conversation when they return to the same ask, and
	// gives two different asks about one strategy two different threads. The thread has no
	// panel beside it yet — the strategy review panel is ADO-1715, and this linked item gains
	// its panel branch when that lands.
	const handleDiscussProposal = useCallback(
		(proposal: PendingProposal, strategyTitle?: string) => {
			trackEvent('Strategy Proposal Discussed', {
				proposal_id: proposal.id,
				change_type: proposal.change_type,
			});
			bridgeApi.sendAction({
				type: 'do_create_chat',
				linked_item: {
					type: 'strategy_proposal',
					id: proposal.id,
					metadata: {
						title: proposalHeadline(proposal),
						strategy_id: proposal.strategy_id,
						...(strategyTitle ? { strategy_title: strategyTitle } : {}),
					},
				},
				initial_message: `About "${proposalHeadline(proposal)}" — talk me through this before I decide.`,
			});
			close();
		},
		[bridgeApi, close],
	);

	// Every press is a fresh reading of the plan, so this opts out of the
	// linked-item dedup rather than returning to an earlier thread about the same
	// strategy: re-pressing used to append another walkthrough request to a
	// conversation that had already answered it, and the agent answered the
	// repeat by repeating itself. The title rides along because a thread per
	// press only stays navigable if each row is named before the LLM gets to it.
	const handleReview = useCallback(
		(strategyId: string, title?: string) => {
			trackEvent('Strategy Reviewed', { strategy_id: strategyId });
			bridgeApi.sendAction({
				type: 'do_create_chat',
				linked_item: {
					type: 'strategy',
					id: strategyId,
					...(title ? { metadata: { title } } : {}),
				},
				initial_message: 'Walk me through this strategy before I activate it.',
				force_new: true,
			});
			close();
		},
		[bridgeApi, close],
	);

	const handleViewSetup = useCallback(
		(processId: string) => {
			trackEvent('Lane2 Viewed', { process_id: processId });
			// Open the campaign panel directly. Starting a chat thread instead left
			// "View my part" landing on prose with no panel, so an owner could not
			// reach the work they had just agreed to do.
			openChatPanel({
				panel_type: 'artifact',
				artifact_type: 'campaign',
				item_id: processId,
			});
		},
		[openChatPanel],
	);

	if (!enabled) return null;

	return (
		<>
			<div
				ref={barRef}
				className={cn(
					// Top-anchored within the header band so the dock reads as a tab that
					// hangs FLUSH from the top edge of the chat chrome, not a floating pill.
					// It stays visible while the menu is open (the orbs persist).
					`pointer-events-none absolute top-0 z-10 flex -translate-x-1/2
					items-start`,
					// Until the column is measured, fall back to container-centered.
					pillLeft === null && 'left-1/2',
				)}
				style={pillLeft === null ? undefined : { left: pillLeft }}
			>
				{/* At rest the bar renders here; while the menu is open it is rendered INSIDE the
				    menu surface (see DockMenu's `bar` slot) so the two are literally one element
				    and the dock paints on top of it. An invisible placeholder keeps this node's
				    measured rect stable for menuRectFrom while open. */}
				{isOpen ? (
					<div aria-hidden className="invisible">
						<DockBar
							items={barItems}
							leavingItems={leavingItems}
							enteringIds={enteringIds}
							settled={settled}
							onOpen={open}
							onCreate={openCreate}
							isOpen
							totalCount={strategies.strategies.length}
							pendingProposalsByStrategyId={pendingProposalsByStrategyId}
						/>
					</div>
				) : (
					<DockBar
						items={barItems}
						leavingItems={leavingItems}
						enteringIds={enteringIds}
						settled={settled}
						onOpen={open}
						onCreate={openCreate}
						isOpen={false}
						totalCount={strategies.strategies.length}
					/>
				)}
			</div>
			{isOpen && rect && (
				<DockMenu
					rect={rect}
					items={visibleItems}
					totalCount={strategies.strategies.length}
					loading={strategies.loading}
					error={strategies.error}
					businessId={userId}
					initialView={intent}
					focusNonce={focusNonce}
					animKind={animKindRef.current}
					// The REAL bar, rendered on top of the menu surface so the dock and the menu
					// are one connected element (the placeholder above only reserves its rect).
					bar={
						<DockBar
							items={barItems}
							leavingItems={leavingItems}
							enteringIds={enteringIds}
							settled={settled}
							onOpen={open}
							onCreate={openCreate}
							isOpen
							totalCount={strategies.strategies.length}
							pendingProposalsByStrategyId={pendingProposalsByStrategyId}
						/>
					}
					onAcknowledgeFlag={handleAcknowledgeFlag}
					onOpenOwnerAsk={handleOpenOwnerAsk}
					onOpenCampaignSetup={handleOpenCampaignSetup}
					onOpenFlagThread={handleOpenFlagThread}
					onGenerate={handleGenerate}
					onRegenerate={handleRegenerate}
					onActivate={handleActivate}
					onReview={handleReview}
					onPause={handlePause}
					// The list's own "Try again", shown when the list failed to load. It re-reads
					// the list: it used to call generate, so recovering from a failed READ started
					// three new strategy runs.
					onRetry={() => strategies.refresh()}
					onDeleted={() => void strategies.refresh()}
					onViewSetup={handleViewSetup}
					proposals={proposalsState.proposals}
					progressByStrategyId={progressState.byStrategyId}
					onRespondProposal={handleRespondProposal}
					onDiscussProposal={handleDiscussProposal}
					onClose={close}
				/>
			)}
		</>
	);
}
